import React, { useState, useCallback } from 'react';
import { Box, Button, Menu, MenuItem, IconButton } from '@mui/material';
import { MoreVertIcon } from '@/shared/ui/display/EmojiIcon';
import { useSessionTimelineTheme } from '../theme';
import type { EventAction } from '../types/sessionTimeline.types';

interface EventCardActionsProps {
  actions?: readonly EventAction[];
  sessionId: string;
  onActionClick?: (actionId: string, sessionId: string) => void;
}

export function EventCardActions({ actions, sessionId, onActionClick }: EventCardActionsProps) {
  const { getActionContainerSx, getActionButtonSx, getDividerSx, tokens } =
    useSessionTimelineTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleActionClick = useCallback(
    (action: EventAction) => {
      if (action.onClick) {
        action.onClick();
      } else {
        onActionClick?.(action.id, sessionId);
      }
      setAnchorEl(null);
    },
    [onActionClick, sessionId]
  );

  if (!actions || actions.length === 0) {
    return null;
  }

  const showActions = actions.filter((action) => action.default === 'show');
  const collapseActions = actions.filter((action) => action.default === 'collapse');

  const handleMoreClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMoreClose = () => {
    setAnchorEl(null);
  };

  return (
    <Box sx={{ marginTop: 2 }}>
      <Box sx={getActionContainerSx()}>
        {showActions.map((action) => (
          <Button
            key={action.id}
            variant="contained"
            size="small"
            onClick={() => handleActionClick(action)}
            sx={getActionButtonSx(action.color)}
          >
            {action.label}
          </Button>
        ))}
        {collapseActions.length > 0 && (
          <>
            <IconButton
              size="small"
              onClick={handleMoreClick}
              sx={{
                padding: '4px',
                color: tokens.action.moreButton.color,
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
              aria-label="더보기"
              aria-controls={open ? 'more-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={open ? 'true' : undefined}
            >
              <MoreVertIcon sx={{ fontSize: tokens.action.moreButton.size }} />
            </IconButton>
            <Menu
              id="more-menu"
              anchorEl={anchorEl}
              open={open}
              onClose={handleMoreClose}
              MenuListProps={{
                'aria-labelledby': 'more-button',
              }}
            >
              {collapseActions.map((action) => (
                <MenuItem key={action.id} onClick={() => handleActionClick(action)}>
                  {action.label}
                </MenuItem>
              ))}
            </Menu>
          </>
        )}
      </Box>
    </Box>
  );
}
